import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { TOAST } from '@/lib/toastMessages';

export interface Chapter {
  id: string;
  project_id: string;
  title: string;
  summary: string | null;
  summary_word_count: number | null;
  order_index: number;
  status: string;
  word_target: number | null;
  created_at: string;
  updated_at: string;
}

export interface Section {
  id: string;
  chapter_id: string;
  title: string;
  content: string | null;
  order_index: number;
  status: string;
  ai_notes: Record<string, unknown> | null;
  word_target: number | null;
  created_at: string;
  updated_at: string;
}

interface ChaptersData {
  chapters: Chapter[];
  sections: Record<string, Section[]>;
}

async function fetchChaptersFromDb(projectId: string): Promise<ChaptersData> {
  const { data: chaptersData, error: chaptersError } = await supabase
    .from('chapters')
    .select('*')
    .eq('project_id', projectId)
    .order('order_index', { ascending: true });

  if (chaptersError) throw chaptersError;

  const chapters = chaptersData || [];
  let sections: Record<string, Section[]> = {};

  if (chapters.length > 0) {
    const chapterIds = chapters.map((c) => c.id);
    const { data: sectionsData, error: sectionsError } = await supabase
      .from('sections')
      .select('*')
      .in('chapter_id', chapterIds)
      .order('order_index', { ascending: true });

    if (sectionsError) throw sectionsError;

    sections = (sectionsData || []).reduce<Record<string, Section[]>>(
      (acc, section) => {
        if (!acc[section.chapter_id]) {
          acc[section.chapter_id] = [];
        }
        acc[section.chapter_id].push(section as Section);
        return acc;
      },
      {}
    );
  }

  return { chapters, sections };
}

export function useChapters(projectId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = ['chapters', projectId];

  const { data, isLoading: loading } = useQuery({
    queryKey,
    queryFn: () => fetchChaptersFromDb(projectId!),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const chapters = data?.chapters || [];
  const sections = data?.sections || {};

  // Subscribe to realtime updates for cross-tab sync
  useEffect(() => {
    if (!projectId) return;

    const chaptersChannel = supabase
      .channel(`chapters-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chapters',
          filter: `project_id=eq.${projectId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey });
        }
      )
      .subscribe();

    const sectionsChannel = supabase
      .channel(`sections-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sections',
        },
        (payload) => {
          const chapterId = (payload.new as Section)?.chapter_id || (payload.old as Section)?.chapter_id;
          if (chapterId && chapters.some(c => c.id === chapterId)) {
            queryClient.invalidateQueries({ queryKey });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(chaptersChannel);
      supabase.removeChannel(sectionsChannel);
    };
  }, [projectId, queryClient, queryKey, chapters]);

  // Chapter mutations
  const addChapterMutation = useMutation({
    mutationFn: async (title: string) => {
      if (!projectId) throw new Error('No project ID');

      const maxIndex = chapters.length > 0 
        ? Math.max(...chapters.map(c => c.order_index)) 
        : -1;

      const { data, error } = await supabase
        .from('chapters')
        .insert({
          project_id: projectId,
          title,
          order_index: maxIndex + 1,
          status: 'outline',
        })
        .select()
        .single();

      if (error) throw error;
      return data as Chapter;
    },
    onSuccess: (newChapter) => {
      queryClient.setQueryData<ChaptersData>(queryKey, (old) => ({
        chapters: [...(old?.chapters || []), newChapter],
        sections: old?.sections || {},
      }));
      toast.success(TOAST.CHAPTER.ADD_SUCCESS.title);
    },
    onError: (err) => {
      console.error('Error adding chapter:', err);
      toast.error(TOAST.CHAPTER.ADD_ERROR.description);
    },
  });

  const updateChapterMutation = useMutation({
    mutationFn: async ({ chapterId, updates }: { 
      chapterId: string; 
      updates: Partial<Pick<Chapter, 'title' | 'summary' | 'summary_word_count' | 'status' | 'word_target'>> 
    }) => {
      const { error } = await supabase
        .from('chapters')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', chapterId);

      if (error) throw error;
      return { chapterId, updates };
    },
    onMutate: async ({ chapterId, updates }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ChaptersData>(queryKey);

      queryClient.setQueryData<ChaptersData>(queryKey, (old) => ({
        chapters: (old?.chapters || []).map(c => 
          c.id === chapterId ? { ...c, ...updates } : c
        ),
        sections: old?.sections || {},
      }));

      return { previous };
    },
    onError: (err, _, context) => {
      console.error('Error updating chapter:', err);
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      toast.error(TOAST.CHAPTER.UPDATE_ERROR.description);
    },
  });

  const deleteChapterMutation = useMutation({
    mutationFn: async (chapterId: string) => {
      const { error } = await supabase
        .from('chapters')
        .delete()
        .eq('id', chapterId);

      if (error) throw error;
      return chapterId;
    },
    onMutate: async (chapterId) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ChaptersData>(queryKey);

      queryClient.setQueryData<ChaptersData>(queryKey, (old) => {
        const newSections = { ...(old?.sections || {}) };
        delete newSections[chapterId];
        return {
          chapters: (old?.chapters || []).filter(c => c.id !== chapterId),
          sections: newSections,
        };
      });

      return { previous };
    },
    onSuccess: () => {
      toast.success(TOAST.CHAPTER.DELETE_SUCCESS.title);
    },
    onError: (err, _, context) => {
      console.error('Error deleting chapter:', err);
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      toast.error(TOAST.CHAPTER.DELETE_ERROR.description);
    },
  });

  const reorderChaptersMutation = useMutation({
    mutationFn: async (reorderedChapters: Chapter[]) => {
      const updates = reorderedChapters.map((chapter, index) => ({
        id: chapter.id,
        order_index: index,
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('chapters')
          .update({ order_index: update.order_index })
          .eq('id', update.id);
        if (error) throw error;
      }

      return reorderedChapters;
    },
    onMutate: async (reorderedChapters) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ChaptersData>(queryKey);

      queryClient.setQueryData<ChaptersData>(queryKey, (old) => ({
        chapters: reorderedChapters.map((c, i) => ({ ...c, order_index: i })),
        sections: old?.sections || {},
      }));

      return { previous };
    },
    onError: (err, _, context) => {
      console.error('Error reordering chapters:', err);
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      toast.error(TOAST.CHAPTER.REORDER_ERROR.description);
    },
  });

  // Section mutations
  const addSectionMutation = useMutation({
    mutationFn: async ({ chapterId, title }: { chapterId: string; title: string }) => {
      const chapterSections = sections[chapterId] || [];
      const maxIndex = chapterSections.length > 0
        ? Math.max(...chapterSections.map(s => s.order_index))
        : -1;

      const { data, error } = await supabase
        .from('sections')
        .insert({
          chapter_id: chapterId,
          title,
          order_index: maxIndex + 1,
          status: 'outline',
        })
        .select()
        .single();

      if (error) throw error;
      return { section: data as Section, chapterId };
    },
    onSuccess: ({ section, chapterId }) => {
      queryClient.setQueryData<ChaptersData>(queryKey, (old) => ({
        chapters: old?.chapters || [],
        sections: {
          ...(old?.sections || {}),
          [chapterId]: [...(old?.sections?.[chapterId] || []), section],
        },
      }));
      toast.success(TOAST.SECTION.ADD_SUCCESS.title);
    },
    onError: (err) => {
      console.error('Error adding section:', err);
      toast.error(TOAST.SECTION.ADD_ERROR.description);
    },
  });

  const updateSectionMutation = useMutation({
    mutationFn: async ({ sectionId, chapterId, updates }: { 
      sectionId: string; 
      chapterId: string;
      updates: Partial<Pick<Section, 'title' | 'content' | 'status' | 'word_target'>> 
    }) => {
      const { error } = await supabase
        .from('sections')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', sectionId);

      if (error) throw error;
      return { sectionId, chapterId, updates };
    },
    onMutate: async ({ sectionId, chapterId, updates }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ChaptersData>(queryKey);

      queryClient.setQueryData<ChaptersData>(queryKey, (old) => ({
        chapters: old?.chapters || [],
        sections: {
          ...(old?.sections || {}),
          [chapterId]: (old?.sections?.[chapterId] || []).map(s =>
            s.id === sectionId ? { ...s, ...updates } : s
          ),
        },
      }));

      return { previous };
    },
    onError: (err, _, context) => {
      console.error('Error updating section:', err);
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      toast.error(TOAST.SECTION.UPDATE_ERROR.description);
    },
  });

  const deleteSectionMutation = useMutation({
    mutationFn: async ({ sectionId, chapterId }: { sectionId: string; chapterId: string }) => {
      const { error } = await supabase
        .from('sections')
        .delete()
        .eq('id', sectionId);

      if (error) throw error;
      return { sectionId, chapterId };
    },
    onMutate: async ({ sectionId, chapterId }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ChaptersData>(queryKey);

      queryClient.setQueryData<ChaptersData>(queryKey, (old) => ({
        chapters: old?.chapters || [],
        sections: {
          ...(old?.sections || {}),
          [chapterId]: (old?.sections?.[chapterId] || []).filter(s => s.id !== sectionId),
        },
      }));

      return { previous };
    },
    onSuccess: () => {
      toast.success(TOAST.SECTION.DELETE_SUCCESS.title);
    },
    onError: (err, _, context) => {
      console.error('Error deleting section:', err);
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      toast.error(TOAST.SECTION.DELETE_ERROR.description);
    },
  });

  const reorderSectionsMutation = useMutation({
    mutationFn: async ({ chapterId, reorderedSections }: { chapterId: string; reorderedSections: Section[] }) => {
      const updates = reorderedSections.map((section, index) => ({
        id: section.id,
        order_index: index,
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('sections')
          .update({ order_index: update.order_index })
          .eq('id', update.id);
        if (error) throw error;
      }

      return { chapterId, reorderedSections };
    },
    onMutate: async ({ chapterId, reorderedSections }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ChaptersData>(queryKey);

      queryClient.setQueryData<ChaptersData>(queryKey, (old) => ({
        chapters: old?.chapters || [],
        sections: {
          ...(old?.sections || {}),
          [chapterId]: reorderedSections.map((s, i) => ({ ...s, order_index: i })),
        },
      }));

      return { previous };
    },
    onError: (err, _, context) => {
      console.error('Error reordering sections:', err);
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      toast.error(TOAST.SECTION.REORDER_ERROR.description);
    },
  });

  // Wrapper functions to maintain existing API
  const addChapter = async (title: string) => {
    try {
      return await addChapterMutation.mutateAsync(title);
    } catch {
      return null;
    }
  };

  const updateChapter = async (
    chapterId: string,
    updates: Partial<Pick<Chapter, 'title' | 'summary' | 'summary_word_count' | 'status' | 'word_target'>>
  ) => {
    try {
      await updateChapterMutation.mutateAsync({ chapterId, updates });
      return true;
    } catch {
      return false;
    }
  };

  const deleteChapter = async (chapterId: string) => {
    try {
      await deleteChapterMutation.mutateAsync(chapterId);
      return true;
    } catch {
      return false;
    }
  };

  const reorderChapters = async (reorderedChapters: Chapter[]) => {
    try {
      await reorderChaptersMutation.mutateAsync(reorderedChapters);
    } catch {
      // Error handled in mutation
    }
  };

  const addSection = async (chapterId: string, title: string) => {
    try {
      const result = await addSectionMutation.mutateAsync({ chapterId, title });
      return result.section;
    } catch {
      return null;
    }
  };

  const updateSection = async (
    sectionId: string,
    chapterId: string,
    updates: Partial<Pick<Section, 'title' | 'content' | 'status' | 'word_target'>>
  ) => {
    try {
      await updateSectionMutation.mutateAsync({ sectionId, chapterId, updates });
      return true;
    } catch {
      return false;
    }
  };

  const deleteSection = async (sectionId: string, chapterId: string) => {
    try {
      await deleteSectionMutation.mutateAsync({ sectionId, chapterId });
      return true;
    } catch {
      return false;
    }
  };

  const reorderSections = async (chapterId: string, reorderedSections: Section[]) => {
    try {
      await reorderSectionsMutation.mutateAsync({ chapterId, reorderedSections });
    } catch {
      // Error handled in mutation
    }
  };

  return {
    chapters,
    sections,
    loading,
    fetchChapters: () => queryClient.invalidateQueries({ queryKey }),
    addChapter,
    updateChapter,
    deleteChapter,
    reorderChapters,
    addSection,
    updateSection,
    deleteSection,
    reorderSections,
  };
}
